import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
} from '@mui/material';
import UserBadgesPopup from './Popups/UserBadgesPopup/UserBadgesPopup';
import ChangePasswordPopup from './Popups/UserAdminPopup/ChangePasswordPopup';
import Api from '../../utils/Api';
import { RoleIds } from '../../policies/Role';

// Page modifier par Frédérick BETHERMAT - 24-03-2026

class UserGrid extends React.Component {
  constructor(props) {
    super(props);

    this.badgeAssignationPopup = React.createRef();

    this.state = {
      open: false,
      openPasswordPopup: false,
      confirmDelete: false,
      selectedUser: null,
      searchValue: null,
      inputValue: '',
    };
  }

  handleBadgeManagement = (user) => {
    this.setState({ open: true, selectedUser: user });
    this.badgeAssignationPopup.current.refreshBadgesAssignation(true);
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  deleteUser = () => {
    const { selectedUser } = this.state;
    Api.delete(`/user/${selectedUser.id}`)
      .then(() => {
        this.props.refreshUsers?.();
        this.setState({ confirmDelete: false, selectedUser: null });
      })
      .catch(() => {
        alert('Erreur lors de la suppression.');
      });
  };

  toggleUserStatus = (user) => {
    Api.post(`/user/${user.id}/toggle-active`)
      .then(() => {
        this.props.refreshAllUsers?.();
      })
      .catch(() => {
        alert('Erreur lors du changement de statut.');
      });
  };

  getAdminColumns() {
    return [
      {
        field: 'role_id',
        headerName: 'Rôle',
        flex: 1,
        valueGetter: (params) => {
          const roles = {
            1: 'Administrateur',
            2: 'Contact Admin',
            3: 'Professeur',
            4: 'Étudiant',
          };
          return roles[params] || 'Inconnu';
        },
      },
      { field: 'first_name', headerName: 'Prénom', flex: 1 },
      { field: 'last_name', headerName: 'Nom', flex: 1 },
      {
        field: 'active',
        headerName: 'Statut',
        flex: 1,
        valueGetter: (params) => (params === 1 ? 'Actif' : 'Désactivé'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 2,
        renderCell: (params) => (
          <Select value="" displayEmpty fullWidth renderValue={() => 'Actions'}>
            <MenuItem onClick={() => this.handleBadgeManagement(params.row)}>
              Gestion des badges
            </MenuItem>
            <MenuItem
              onClick={() =>
                this.setState({
                  openPasswordPopup: true,
                  selectedUser: params.row,
                })
              }
            >
              Modifier le mot de passe
            </MenuItem>
            <MenuItem
              onClick={() =>
                this.setState({ confirmDelete: true, selectedUser: params.row })
              }
            >
              Supprimer
            </MenuItem>
            <MenuItem onClick={() => this.toggleUserStatus(params.row)}>
              {params.row.active === 1 ? 'Désactiver' : 'Activer'}
            </MenuItem>
          </Select>
        ),
      },
    ];
  }

  getTeacherColumns() {
    return [
      { field: 'first_name', headerName: 'Prénom', flex: 1 },
      { field: 'last_name', headerName: 'Nom', flex: 1 },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            onClick={() => this.handleBadgeManagement(params.row)}
          >
            Assigner badge
          </Button>
        ),
      },
    ];
  }

  render() {
    const { role, rows } = this.props;
    const isTeacher = role === RoleIds.Teacher;

    let filteredRows = [...(rows ?? [])];

    // filtre enseignant
    if (isTeacher) {
      filteredRows = filteredRows.filter(
        (user) => user.role_id === RoleIds.Student,
      );
    }

    // tri alphabétique
    filteredRows.sort((a, b) => {
      const nomA = (a.last_name || '').toLowerCase();
      const nomB = (b.last_name || '').toLowerCase();

      if (nomA < nomB) return -1;
      if (nomA > nomB) return 1;

      const prenomA = (a.first_name || '').toLowerCase();
      const prenomB = (b.first_name || '').toLowerCase();

      return prenomA.localeCompare(prenomB);
    });

    // options autocomplete
    const options = filteredRows.map(
      (user) => `${user.first_name} ${user.last_name}`,
    );

    // filtre recherche
    let displayedRows = filteredRows;

    if (this.state.inputValue) {
      const search = this.state.inputValue.toLowerCase();

      displayedRows = filteredRows.filter((user) =>
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(search),
      );
    }

    const columns = isTeacher
      ? this.getTeacherColumns()
      : this.getAdminColumns();

    return (
      <div style={{ width: '100%' }}>
        {/* 🔍 BARRE DE RECHERCHE */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            options={options}
            inputValue={this.state.inputValue}
            onInputChange={(event, newInputValue) => {
              this.setState({ inputValue: newInputValue });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Rechercher un utilisateur" />
            )}
          />
        </Box>

        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={displayedRows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </div>

        <UserBadgesPopup
          isOpen={this.state.open}
          selectedUser={this.state.selectedUser}
          handleClose={this.handleClose}
          ref={this.badgeAssignationPopup}
        />

        {this.state.selectedUser && !isTeacher && (
          <ChangePasswordPopup
            isOpen={this.state.openPasswordPopup}
            handleClose={() => this.setState({ openPasswordPopup: false })}
            userId={this.state.selectedUser.id}
          />
        )}

        <Dialog
          open={this.state.confirmDelete}
          onClose={() => this.setState({ confirmDelete: false })}
        >
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            Êtes-vous sûr de vouloir supprimer{' '}
            {this.state.selectedUser?.first_name}{' '}
            {this.state.selectedUser?.last_name} ?
          </DialogContent>
          <DialogActions>
            <Button onClick={this.deleteUser} color="error" variant="contained">
              Confirmer
            </Button>
            <Button
              onClick={() => this.setState({ confirmDelete: false })}
              variant="outlined"
            >
              Annuler
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default UserGrid;
